import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";

import {
  type PersonState,
  type PersonWithIdentityWithheld,
} from "@thalamos/haven-common";

import api from "../../api/index.js";
import { AllocateResponsibleUserDialog } from "../../components/InvolvementDialogs/InvolvementDialogs.js";
import { createRoute } from "../../routing/createRoute.js";
import { CaseInvolvementTab } from "../Persons/CaseInvolvementTab.js";
import { adminInvolvementManagement } from "./adminInvolvementManagement.js";
import { parsePersonReference } from "./parsePersonReference.js";

type Reading =
  | { kind: "idle" }
  | { kind: "not_a_reference" }
  | { kind: "reading" }
  | { kind: "not_found" }
  | { kind: "failed" }
  | { kind: "found"; view: PersonWithIdentityWithheld };

const Summary = ({ view }: { view: PersonWithIdentityWithheld }) => {
  const { t } = useTranslation();

  const state = (personState: PersonState) =>
    match(personState)
      .with({ kind: "unassigned" }, ({ missing }) =>
        t("personPage.unassigned.banner", {
          missing: missing.map((kind) => t(`access.kind.${kind}`)).join(", "),
        }),
      )
      .with({ kind: "active" }, { kind: "archived" }, ({ kind }) =>
        t(`personPage.state.${kind}`),
      )
      .exhaustive();

  const fields = [
    { label: t("admin.persons.personId"), value: view.personId },
    { label: t("admin.persons.state"), value: state(view.state) },
    {
      label: t("admin.persons.openedBy"),
      value: view.openedBy.map(({ name }) => name).join(", "),
    },
  ];

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={4}
      sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}
    >
      {fields.map(({ label, value }) => (
        <Box key={label}>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="body2">{value}</Typography>
        </Box>
      ))}
    </Stack>
  );
};

/**
 * A Thalamos admin reading a young person's record by the id or link a
 * customer gave, and deciding who is involved with it, with the child's
 * identity withheld.
 */
export const AdminPersonPage = () => {
  const { t } = useTranslation();
  const [reference, setReference] = useState("");
  const [reading, setReading] = useState<Reading>({ kind: "idle" });
  const [allocatingResponsibleUser, setAllocatingResponsibleUser] =
    useState(false);

  const find = async () => {
    const personId = parsePersonReference(reference);

    if (personId === null) {
      setReading({ kind: "not_a_reference" });
      return;
    }

    setReading({ kind: "reading" });
    const answer = await api.adminPerson.readPerson(personId);

    setReading(
      match(answer)
        .with({ kind: "view" }, ({ view }): Reading => ({
          kind: "found",
          view,
        }))
        .with({ kind: "refused", status: 404 }, (): Reading => ({
          kind: "not_found",
        }))
        .with(
          { kind: "refused" },
          { kind: "malformed" },
          { kind: "unanswered" },
          (): Reading => ({ kind: "failed" }),
        )
        .exhaustive(),
    );
  };

  const shown = useMemo(
    () =>
      reading.kind === "found"
        ? {
            view: reading.view,
            ...adminInvolvementManagement({
              view: reading.view,
              api: {
                findUsers: api.user.find,
                findOrganisations: api.organisation.find,
                ...api.adminPerson,
              },
              onView: (view) => {
                setReading({ kind: "found", view });
              },
            }),
          }
        : undefined,
    [reading],
  );

  return (
    <Stack spacing={3}>
      <Typography variant="h5" component="h1">
        {t("admin.persons.title")}
      </Typography>

      <Stack
        component="form"
        direction="row"
        spacing={2}
        alignItems="flex-start"
        onSubmit={(event) => {
          event.preventDefault();
          void find();
        }}
      >
        <TextField
          label={t("admin.persons.reference")}
          value={reference}
          onChange={(event) => {
            setReference(event.target.value);
          }}
          error={reading.kind === "not_a_reference"}
          helperText={
            reading.kind === "not_a_reference"
              ? t("admin.persons.notAReference")
              : undefined
          }
          autoComplete="off"
          sx={{ flexGrow: 1, maxWidth: 640 }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={reading.kind === "reading"}
          sx={{ minHeight: 56 }}
        >
          {t("admin.persons.open")}
        </Button>
      </Stack>

      {match(reading)
        .with({ kind: "not_found" }, () => (
          <Alert severity="warning">{t("admin.persons.notFound")}</Alert>
        ))
        .with({ kind: "failed" }, () => (
          <Alert severity="error">{t("admin.persons.failed")}</Alert>
        ))
        .with(
          { kind: "idle" },
          { kind: "not_a_reference" },
          { kind: "reading" },
          { kind: "found" },
          () => null,
        )
        .exhaustive()}

      {shown === undefined ? null : (
        <>
          <Alert severity="info">{t("admin.persons.identityWithheld")}</Alert>
          <Summary view={shown.view} />
          <CaseInvolvementTab
            personName={t("admin.persons.thisPerson")}
            state={shown.view.state}
            involvements={shown.view.involvements}
            involvementsFailed={false}
            // A Thalamos admin reads no assessment, so nothing is held back from
            // ending here; the server still refuses such an ending.
            assessments={undefined}
            actor={shown.actor}
            management={shown.management}
            onAllocateResponsibleUser={
              shown.allocation === undefined
                ? undefined
                : () => {
                    setAllocatingResponsibleUser(true);
                  }
            }
            // A Thalamos admin ends a guest's access and opens nothing else
            // about them.
            guests={{
              guests: shown.view.guests,
              failed: false,
              mayOpenDetails: () => false,
              onEnd: shown.onEndGuest,
              assessmentHref: createRoute.assessment,
            }}
          />
          {shown.allocation === undefined ? null : (
            <AllocateResponsibleUserDialog
              open={allocatingResponsibleUser}
              onClose={() => {
                setAllocatingResponsibleUser(false);
              }}
              search={shown.allocation}
              onAllocateResponsibleUser={
                shown.allocation.onAllocateResponsibleUser
              }
              onRefresh={shown.allocation.onRefresh}
            />
          )}
        </>
      )}
    </Stack>
  );
};
